import { getCoreValidators } from './coreValidators'
import { getStCeloValidatorGroups } from './celoValidatorGroups'

/**
 * Validator / delegation dataset for LST deposits.
 *
 * A few LSTs require (or allow) the depositor to pick the validator,
 * validator-group, node, or pool the staked asset is delegated to — unlike
 * the pooled majority (Lido, Rocket Pool, EtherFi, …) where the protocol
 * decides. This module is the single source of truth for:
 *
 *  1. **the descriptor** ({@link LstDelegation}) — *whether* a selection is
 *     needed, the deposit option key to send the choice back as, whether a
 *     safe default exists, and where the list comes from; and
 *  2. **the live set** ({@link LstValidator}) — the selectable validators with
 *     status + capacity, normalised across protocols.
 *
 * The descriptor mirrors `acceptedInputs` (the pay-asset accept-set): a
 * machine-readable answer to "do I have to pick a validator, and from where".
 * Both are attached to each LST by the vault fetcher (`fetchLstShareTokens`).
 */

export type LstDelegationKind =
  | 'validator' // Core (operator), TruFin
  | 'validatorGroup' // Celo (StakedCelo group)
  | 'node' // Lair (Kaia Governance Council node)
  | 'pool' // Solv (poolId)
  | 'vault' // StakeWise (per-vault)

/** Descriptor of an LST's validator-selection requirement. */
export interface LstDelegation {
  /** The integrator MUST supply a choice — no safe auto/default. (Core,
   *  Solv.) When `false`, omitting the choice still produces a working tx
   *  via `default` or server-side auto-resolution (Celo, Lair, TruFin). */
  required: boolean
  /** What kind of target is being chosen. */
  kind: LstDelegationKind
  /** The deposit option query param to send the chosen `id` back as
   *  (e.g. `validator`, `validatorGroup`, `node`, `poolId`, `vault`). */
  optionKey: string
  /** A working default `id` when `required` is false; `'auto'` when the
   *  server resolves one (Celo); `null` when the protocol picks internally
   *  or a value is mandatory. */
  default?: string | null
  /** `endpoint` ⇒ the live set is on-chain and returned in `validators`;
   *  `offchain` ⇒ the value comes from the protocol's docs/API (Solv
   *  poolId), so `validators` is empty. */
  source: 'endpoint' | 'offchain'
}

/** A normalised, selectable delegation target. `id` is the opaque value to
 *  pass back as the deposit option named by {@link LstDelegation.optionKey}. */
export interface LstValidator {
  /** Value to pass back to the deposit endpoint (validator / group / node
   *  address, or pool id). */
  id: string
  /** Operational status. Only `active` targets are `selectable`. */
  status: 'active' | 'inactive' | 'jailed' | 'full'
  /** Passes the protocol's eligibility (healthy + not blocked + has room). */
  selectable: boolean
  /** Our default pick (best capacity / health) — lets a UI preselect. */
  recommended: boolean
  /** Human label, when the target has one (e.g. the Solv pool's pay currency
   *  `"WBTC"`). Absent for bare validator addresses. */
  name?: string
  /** For `kind:'pool'` (Solv): the pay-currency address this pool subscribes
   *  with — the deposit's `payAsset` must match it. */
  currency?: string
  /** Remaining stake the target can absorb, raw underlying as string.
   *  Populated where the protocol exposes it (Celo `getReceivableVotesForGroup`). */
  receivableVotes?: string
}

/** A Solv OpenFundMarket subscription pool. `poolId` is `createSubscription`'s
 *  first arg; it's verifiable on-chain via `OpenFundMarket.poolInfos(poolId)`
 *  (`.currency` must equal `currency`). Solv publishes these off-chain
 *  (docs.solv.finance), so they're curated here and surfaced through the same
 *  delegation flow as on-chain validators. */
interface SolvPool {
  poolId: string
  /** Pay-currency (the deposit `payAsset`) this pool subscribes with. */
  currency: string
  /** Currency symbol label for UI. */
  name: string
}

/**
 * Solv subscription pools per `(chainId, shareToken)`. Each `poolId` is
 * verified against `OpenFundMarket.poolInfos(poolId).currency`. The first
 * entry is the canonical pool (the share token's `underlying`) and is used as
 * the deposit's auto-filled `default`.
 */
const SOLV_POOLS: { [chainId: string]: { [shareToken: string]: SolvPool[] } } =
  {
    '1': {
      // SolvBTC — subscribable with WBTC (canonical) or cbBTC; same SolvBTC SFT.
      '0x7a56e1c57c7475ccf742a1832b028f0456652f97': [
        {
          poolId:
            '0x716db7dc196abe78d5349c7166896f674ab978af26ada3e5b3ea74c5a1b48307',
          currency: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', // WBTC
          name: 'WBTC',
        },
        {
          poolId:
            '0xdc0937dd33c4af08a08724da23bc45b33b43fbb23f365e7b50a536ce45f447ef',
          currency: '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf', // cbBTC
          name: 'cbBTC',
        },
      ],
      // xSolvBTC — the SolvBTC.LST: subscribes with SolvBTC (not WBTC). Verified
      // via SolvBTCYieldTokenMultiAssetPool.getERC20(sft, slot) == xSolvBTC.
      '0xd9d920aa40f578ab794426f5c90f6c731d159def': [
        {
          poolId:
            '0xefcca1eb946cdc7b56509489a56b45b75aff74b8bb84dad5b893012157e0df93',
          currency: '0x7a56e1c57c7475ccf742a1832b028f0456652f97', // SolvBTC
          name: 'SolvBTC',
        },
      ],
    },
  }

/**
 * Per-chain, per-share-token delegation descriptors. The catalogue of which
 * LSTs need a validator selection. Extend as protocols are wired.
 */
const LST_DELEGATION: {
  [chainId: string]: { [shareToken: string]: LstDelegation }
} = {
  // Core DAO — the validator is mandatory for both stCORE (`Earn.mint(validator)`)
  // and SCORE (`deposit(validator)`); dualCORE picks internally (no selection).
  '1116': {
    // stCORE
    '0xb3a8f0f0da9ffc65318aa39e55079796093029ad': {
      required: true,
      kind: 'validator',
      optionKey: 'validator',
      default: null,
      source: 'endpoint',
    },
    // SCORE (StakedCore) — its own staking contract, same Core validator set.
    '0xa20b3b97df3a02f9185175760300a06b4e0a2c05': {
      required: true,
      kind: 'validator',
      optionKey: 'validator',
      default: null,
      source: 'endpoint',
    },
  },
  // Celo — StakedCelo: optional from the integrator's view (the server
  // auto-resolves a valid group when omitted), but the on-chain default
  // strategy reverts, so a group must ultimately be chosen.
  '42220': {
    '0xc668583dcbdc9ae6fa3ce46462758188adfdfc24': {
      required: false,
      kind: 'validatorGroup',
      optionKey: 'validatorGroup',
      default: 'auto',
      source: 'endpoint',
    },
  },
  // Solv (Ethereum). SolvBTC's pools are curated + verified (`SOLV_POOLS`), so
  // it surfaces a list and auto-fills the canonical (WBTC) poolId. xSolvBTC /
  // BTC+ still need their poolIds catalogued → off-chain until then.
  '1': {
    '0x7a56e1c57c7475ccf742a1832b028f0456652f97': {
      required: false, // canonical WBTC poolId is auto-filled
      kind: 'pool',
      optionKey: 'poolId',
      default:
        '0x716db7dc196abe78d5349c7166896f674ab978af26ada3e5b3ea74c5a1b48307',
      source: 'endpoint',
    },
    '0xd9d920aa40f578ab794426f5c90f6c731d159def': {
      required: false, // canonical SolvBTC poolId is auto-filled
      kind: 'pool',
      optionKey: 'poolId',
      default:
        '0xefcca1eb946cdc7b56509489a56b45b75aff74b8bb84dad5b893012157e0df93',
      source: 'endpoint',
    },
    '0xcea2daf93617b97504e05affc5bcf9b3922d3034': {
      required: true,
      kind: 'pool',
      optionKey: 'poolId',
      default: null,
      source: 'offchain',
    },
  },
}

/** The delegation descriptor for an LST, or `undefined` when it is pooled
 *  (no validator selection — the vast majority). */
export const getLstDelegation = (
  chainId: string,
  shareToken: string,
): LstDelegation | undefined =>
  LST_DELEGATION[chainId]?.[shareToken.toLowerCase()]

/**
 * Fetch the live, selectable validator set for an LST, normalised across
 * protocols. Returns `[]` for pooled LSTs, off-chain selections (Solv), or
 * when the on-chain read fails (best-effort, never throws).
 */
export const getLstValidators = async (
  chainId: string,
  shareToken: string,
): Promise<LstValidator[]> => {
  const d = getLstDelegation(chainId, shareToken)
  if (!d || d.source !== 'endpoint') return []
  try {
    if (d.kind === 'pool') {
      // Curated Solv pools — no RPC; the canonical (underlying) pool first.
      const pools = SOLV_POOLS[chainId]?.[shareToken.toLowerCase()] ?? []
      return pools.map((p, i) => ({
        id: p.poolId,
        status: 'active',
        selectable: true,
        recommended: i === 0,
        name: p.name,
        currency: p.currency,
      }))
    }
    if (d.kind === 'validatorGroup' && chainId === '42220') {
      const groups = await getStCeloValidatorGroups()
      return groups.map((g, i) => ({
        id: g.group,
        status: 'active',
        selectable: true,
        recommended: i === 0,
        receivableVotes: g.receivableVotes,
      }))
    }
    if (d.kind === 'validator' && chainId === '1116') {
      // Only the currently-elected validators are selectable (a non-elected
      // candidate stakes fine but earns nothing until elected).
      const { validators } = await getCoreValidators()
      return validators.map((v, i) => ({
        id: v,
        status: 'active',
        selectable: true,
        recommended: i === 0,
      }))
    }
  } catch {
    return []
  }
  return []
}
